import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import BonusService, { ClaimableBonus } from '../../services/bonusService';
import '../../styles/components/BonusNotification.css';

const BonusNotification: React.FC = () => {
  const [bonuses, setBonuses] = useState<ClaimableBonus[]>([]);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const fetchClaimableBonuses = useCallback(async () => {
    if (!user) return;

    try {
      // Use centralized bonus service with caching
      const bonusService = BonusService.getInstance();
      const data = await bonusService.getClaimableBonuses(user.id);

      if (data && data.length > 0) {
        setBonuses(data);
        setShowNotification(true);

        // Mark notifications as shown
        for (const bonus of data) {
          if (!bonus.notification_shown) {
            await supabase.rpc('mark_notification_shown', { p_bonus_id: bonus.id });
          }
        }
      }
    } catch (err) {
      console.error('Error fetching claimable bonuses:', err);
    }
  }, [user]);

  useEffect(() => {
    // Removed fetchClaimableBonuses() call - TopNavbar now handles this globally
    // with caching and periodic updates every 5 minutes to prevent duplicate calls
  }, []);

  const handleClaimClick = () => {
    navigate('/claim-bonuses');
    setShowNotification(false);
  };

  const handleDismiss = () => {
    setShowNotification(false);
  };

  if (!showNotification || bonuses.length === 0) {
    return null;
  }

  const totalPoints = bonuses.reduce((sum, bonus) => sum + bonus.points, 0);
  const bonusCount = bonuses.length;

  return (
    <div className="bonus-notification-container">
      <div className="bonus-notification">
        <div className="notification-header">
          <div className="notification-icon">🎁</div>
          <h4>{t('bonusNotification.title')}</h4>
          <button className="close-button" onClick={handleDismiss}>×</button>
        </div>
        <div className="notification-content">
          <p>{bonusCount === 1 ? t('bonusNotification.youHave', { count: bonusCount }) : t('bonusNotification.youHave_plural', { count: bonusCount })}</p>
          <p className="points-available">{t('bonusNotification.pointsAvailable', { points: totalPoints })}</p>
        </div>
        <div className="notification-footer">
          <button className="claim-button" onClick={handleClaimClick}>
            {t('bonusNotification.claimNow')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BonusNotification;
